import type { McpAccess, OpsMcpCall } from "./types";

export interface PlanDiffLine {
  path: string;
  kind: "added" | "removed" | "changed";
  before?: string;
  after?: string;
}

const show = (value: unknown) => (typeof value === "string" ? value : JSON.stringify(value));

/** Objects become dot paths (`goal.target`); arrays and scalars are leaves, shown as written. */
function flatten(value: unknown, prefix: string, out: Map<string, string>): Map<string, string> {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0 && prefix) out.set(prefix, "{}");
    for (const [key, v] of entries) flatten(v, prefix ? `${prefix}.${key}` : key, out);
  } else if (prefix) {
    out.set(prefix, show(value));
  }
  return out;
}

/** What one plan change did, field by field; a created plan (before = null) is all additions. */
export function diffPlan(before: Record<string, unknown> | null, after: Record<string, unknown>): PlanDiffLine[] {
  const a = flatten(before ?? {}, "", new Map());
  const b = flatten(after, "", new Map());
  const lines: PlanDiffLine[] = [];
  for (const [path, value] of b) {
    if (!a.has(path)) lines.push({ path, kind: "added", after: value });
    else if (a.get(path) !== value) lines.push({ path, kind: "changed", before: a.get(path), after: value });
  }
  for (const [path, value] of a) if (!b.has(path)) lines.push({ path, kind: "removed", before: value });
  return lines.sort((x, y) => x.path.localeCompare(y.path));
}

/** Who made a plan change: `mcp:<email>` from the CS MCP server, or the seeding script. */
export function changedBy(by: string): string {
  if (by === "migrate-ops-collection") return "migration script";
  return by.replace(/^mcp:/, "");
}

export const isApiKey = (caller: string) => caller.startsWith("apikey:");

/** Can this call have changed something? Rows stored before tool_access existed say nothing. */
export const isChange = (call: Pick<OpsMcpCall, "tool_access">) => call.tool_access === "propose" || call.tool_access === "operate";

export const ACCESS_TONE: Record<McpAccess, "muted" | "info" | "flat"> = { read: "muted", propose: "info", operate: "flat" };

export const ACCESS_HINT: Record<McpAccess, string> = {
  read: "Reads only",
  propose: "Reads, and files decisions for the board",
  operate: "Every tool, writes included (staff signed in with Clerk)",
};

export const STATUS_LABEL: Record<OpsMcpCall["status"], string> = { ok: "ok", error: "error", unknown_org: "unknown org" };
export const STATUS_TONE: Record<OpsMcpCall["status"], "success" | "error" | "warning"> = { ok: "success", error: "error", unknown_org: "warning" };
